import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Form,
  Input,
  Space,
  Tabs,
  TimePicker,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import HVACSpacesContext from '@totem/components/portfolio/buildings/hvac/HVACSpacesContext';
import {
  BASScheduleEntry,
  DAYS_OF_WEEK,
  DaySchedule,
  HVACSpace,
  ScheduleException,
} from '@totem/components/portfolio/buildings/hvac/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const { Text } = Typography;
const TIME_FORMAT = 'h:mm a';

const emptyDaySchedule = (day: string): DaySchedule => ({
  dayOfWeek: day,
  startTime: '',
  endTime: '',
});

const emptyBASEntry = (day: string): BASScheduleEntry => ({
  dayOfWeek: day,
  startTime: '',
  endTime: '',
  equipment: '',
});

type Props = {
  open: boolean;
  space: HVACSpace;
  onClose: () => void;
};

const timeValue = (val: string) => {
  if (!val) return null;
  // Values entered via the picker are stored as "h:mm a" (e.g. "8:00 am").
  // Values written by the Excel import are stored as "HH:mm" (e.g. "08:00").
  // Try the picker format first, then fall back to 24-hour.
  const t12 = dayjs(val, TIME_FORMAT, true);
  if (t12.isValid()) return t12;
  const t24 = dayjs(val, 'HH:mm', true);
  return t24.isValid() ? t24 : null;
};

const HVACScheduleDrawer = ({ open, space, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { buildingId, requestRefresh } = useContext(HVACSpacesContext);
  const [isSending, setIsSending] = useState(false);

  const [leaseSchedule, setLeaseSchedule] = useState<DaySchedule[]>([]);
  const [basSchedule, setBASSchedule] = useState<BASScheduleEntry[]>([]);
  const [leaseExceptions, setLeaseExceptions] = useState<ScheduleException[]>(
    [],
  );

  useEffect(() => {
    if (open && space) {
      setLeaseSchedule(
        DAYS_OF_WEEK.map((day) => {
          const existing = (space.leaseSchedule ?? []).find(
            (sch) => sch.dayOfWeek === day,
          );
          return existing ?? emptyDaySchedule(day);
        }),
      );

      setBASSchedule(
        DAYS_OF_WEEK.map((day) => {
          const existing = (space.basSchedule ?? []).find(
            (sch) => sch.dayOfWeek === day,
          );
          return existing ?? emptyBASEntry(day);
        }),
      );

      setLeaseExceptions(space.leaseExceptions ?? []);
    }
  }, [open, space]);

  const save = useCallback(
    async (endpoint: string, method: string, body: unknown) => {
      if (isSending) return;
      setIsSending(true);

      fetch(endpoint, {
        method,
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(body),
      })
        .then((res) => CheckResponseShowError(res))
        .finally(() => setIsSending(false));
    },
    [isSending, getApiToken],
  );

  const handleSaveLeaseSchedule = () => {
    const endpoint = `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}/lease-schedule`;
    save(endpoint, 'PUT', leaseSchedule).then(() => requestRefresh());
  };

  const handleSaveBASSchedule = () => {
    const endpoint = `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}/bas-schedule`;
    save(endpoint, 'PUT', basSchedule).then(() => requestRefresh());
  };

  const handleAddException = () => {
    setLeaseExceptions([
      ...leaseExceptions,
      { type: '', description: '', scheduleImpact: '' },
    ]);
  };

  const handleSaveException = (idx: number) => {
    const ex = leaseExceptions[idx];
    const endpoint = `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}/lease-exceptions`;
    save(endpoint, 'POST', ex).then(() => requestRefresh());
  };

  const handleRemoveException = (idx: number) => {
    const ex = leaseExceptions[idx];
    if (!ex.type) {
      setLeaseExceptions(leaseExceptions.filter((_, idx2) => idx2 !== idx));
      return;
    }
    const endpoint = `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}/lease-exceptions/${encodeURIComponent(ex.type)}`;
    save(endpoint, 'DELETE', null).then(() => {
      setLeaseExceptions(leaseExceptions.filter((_, idx2) => idx2 !== idx));

      requestRefresh();
    });
  };

  const updateLeaseRow = (
    idx: number,
    field: keyof DaySchedule,
    value: string,
  ) => {
    const updated = [...leaseSchedule];
    updated[idx] = { ...updated[idx], [field]: value };

    setLeaseSchedule(updated);
  };

  const updateBASRow = (
    idx: number,
    field: keyof BASScheduleEntry,
    value: string,
  ) => {
    const updated = [...basSchedule];
    updated[idx] = { ...updated[idx], [field]: value };

    setBASSchedule(updated);
  };

  const updateException = (
    idx: number,
    field: keyof ScheduleException,
    value: string,
  ) => {
    const updated = [...leaseExceptions];
    updated[idx] = { ...updated[idx], [field]: value };

    setLeaseExceptions(updated);
  };

  const scheduleGrid = (
    rows: DaySchedule[],
    onUpdate: (idx: number, field: keyof DaySchedule, value: string) => void,
    onSave: () => void,
    label: string,
  ) => (
    <div>
      <Form layout="vertical">
        {rows.map((row, idx) => (
          <div
            key={row.dayOfWeek}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text style={{ width: 36, flexShrink: 0 }}>{row.dayOfWeek}</Text>
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              use12Hours
              value={timeValue(row.startTime)}
              onChange={(_, str) =>
                onUpdate(idx, 'startTime', Array.isArray(str) ? str[0] : str)
              }
              placeholder="Start"
              style={{ flex: 1 }}
            />
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              use12Hours
              value={timeValue(row.endTime)}
              onChange={(_, str) =>
                onUpdate(idx, 'endTime', Array.isArray(str) ? str[0] : str)
              }
              placeholder="End"
              style={{ flex: 1 }}
            />
          </div>
        ))}
      </Form>
      <Button
        type="primary"
        loading={isSending}
        onClick={onSave}
        style={{ marginTop: 8 }}
      >
        Save {label}
      </Button>
    </div>
  );

  const basGrid = (
    rows: BASScheduleEntry[],
    onUpdate: (
      idx: number,
      field: keyof BASScheduleEntry,
      value: string,
    ) => void,
    onSave: () => void,
  ) => (
    <div>
      <Form layout="vertical">
        {rows.map((row, idx) => (
          <div
            key={row.dayOfWeek}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text style={{ width: 36, flexShrink: 0 }}>{row.dayOfWeek}</Text>
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              use12Hours
              value={timeValue(row.startTime)}
              onChange={(_, str) =>
                onUpdate(idx, 'startTime', Array.isArray(str) ? str[0] : str)
              }
              placeholder="Start"
              style={{ flex: 1 }}
            />
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              use12Hours
              value={timeValue(row.endTime)}
              onChange={(_, str) =>
                onUpdate(idx, 'endTime', Array.isArray(str) ? str[0] : str)
              }
              placeholder="End"
              style={{ flex: 1 }}
            />
            <Input
              value={row.equipment}
              onChange={(evt) => onUpdate(idx, 'equipment', evt.target.value)}
              placeholder="Equipment"
              style={{ flex: 2 }}
            />
          </div>
        ))}
      </Form>
      <Button
        type="primary"
        loading={isSending}
        onClick={onSave}
        style={{ marginTop: 8 }}
      >
        Save BAS Schedule
      </Button>
    </div>
  );

  const exceptionsPanel = (
    <div>
      {leaseExceptions.map((ex, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <Form layout="vertical">
            <Form.Item label="Exception Type" style={{ marginBottom: 8 }}>
              <Input
                value={ex.type}
                onChange={(evt) =>
                  updateException(idx, 'type', evt.target.value)
                }
                placeholder="e.g. holiday, 24/7"
              />
            </Form.Item>
            <Form.Item label="Description" style={{ marginBottom: 8 }}>
              <Input
                value={ex.description}
                onChange={(evt) =>
                  updateException(idx, 'description', evt.target.value)
                }
              />
            </Form.Item>
            <Form.Item label="Schedule Impact" style={{ marginBottom: 8 }}>
              <Input
                value={ex.scheduleImpact}
                onChange={(evt) =>
                  updateException(idx, 'scheduleImpact', evt.target.value)
                }
              />
            </Form.Item>
          </Form>
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={() => handleSaveException(idx)}
              loading={isSending}
            >
              Save
            </Button>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveException(idx)}
              loading={isSending}
            >
              Remove
            </Button>
          </Space>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={handleAddException}>
        Add Exception
      </Button>
    </div>
  );

  const tabItems = [
    {
      key: 'lease',
      label: 'Lease Schedule',
      children: scheduleGrid(
        leaseSchedule,
        updateLeaseRow,
        handleSaveLeaseSchedule,
        'Lease Schedule',
      ),
    },
    {
      key: 'bas',
      label: 'BAS Schedule',
      children: basGrid(basSchedule, updateBASRow, handleSaveBASSchedule),
    },
    {
      key: 'exceptions',
      label: 'Exceptions',
      children: exceptionsPanel,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Schedules — ${space?.name ?? ''}`}
      width={560}
    >
      <Tabs items={tabItems} />
    </Drawer>
  );
};

export default HVACScheduleDrawer;
